import React, { createContext, useContext, useEffect, useState } from 'react';
import { authService } from '@/services/auth';
import { apiClient } from '@/services/api';
import { useRoleContext } from '@/contexts/RoleContext';
import type { User } from '@/types';

// Synchronously check if tokens exist in localStorage (for initial state)
function hasTokensInStorage(): boolean {
  try {
    const stored = localStorage.getItem('cognitoTokens');
    if (!stored) return false;
    const tokens = JSON.parse(stored);
    // Check if tokens exist and aren't expired
    return tokens && tokens.expiresAt > Date.now();
  } catch {
    return false;
  }
}

interface AuthContextType {
  user: User | null;           // The effective user (with role simulation applied if active)
  actualUser: User | null;     // The actual user from the backend (never simulated)
  isLoading: boolean;
  isAuthenticated: boolean;
  login: () => void;
  logout: () => void;
  refreshUser: () => Promise<void>;
  refreshSession: () => Promise<boolean>;  // Refresh the auth tokens
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Inner provider that uses the role context
const AuthProviderInner: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [actualUser, setActualUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Initialize hasValidTokens synchronously from localStorage to prevent redirect flash
  const [hasValidTokens, setHasValidTokens] = useState(() => hasTokensInStorage());
  const roleContext = useRoleContext();

  const loadUser = async () => {
    try {
      const isAuth = await authService.isAuthenticated();
      setHasValidTokens(isAuth);

      if (isAuth) {
        // Fetch user from backend API - this includes proper role evaluation
        // based on identity provider groups configured in the backend
        const backendUser = await apiClient.getCurrentUser();

        setActualUser(backendUser);
      } else {
        setActualUser(null);
      }
    } catch (error) {
      console.error('Error loading user:', error);
      // Don't clear user if we have valid tokens - API might just be temporarily unavailable
      // Only clear if we don't have tokens
      if (!hasValidTokens) {
        setActualUser(null);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadUser();
  }, []);

  const login = () => {
    authService.login();
  };

  const logout = () => {
    setActualUser(null);
    setHasValidTokens(false);
    authService.logout();
  };

  const refreshUser = async () => {
    await loadUser();
  };

  const refreshSession = async (): Promise<boolean> => {
    try {
      const session = await authService.refreshSession();
      return session !== null;
    } catch (error) {
      console.error('Error refreshing session:', error);
      return false;
    }
  };

  // Apply role simulation to get the effective user
  const effectiveUser = roleContext.getEffectiveUser(actualUser);

  const value: AuthContextType = {
    user: effectiveUser,        // The effective user (with simulation if active)
    actualUser,                  // The actual user (never simulated)
    isLoading,
    // User is authenticated if they have valid tokens (even if API call failed)
    isAuthenticated: hasValidTokens,
    login,
    logout,
    refreshUser,
    refreshSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Wrapper that provides the role context first
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <AuthProviderInner>
      {children}
    </AuthProviderInner>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
